/**
 * File: web/src/routes/Upload.tsx
 * ==============================
 *
 * Upload route (authenticated uploader UI entrypoint)
 *
 * Purpose:
 * - Provide the main "Upload" page for end users.
 * - Gate access to the actual upload workflow based on authentication state:
 *   - If not signed in: show guidance and require login.
 *   - If signed in: render <UploadQueue /> which performs the multipart upload flow.
 *
 * When used:
 * - This is the primary landing route for users who need to submit files.
 * - It is typically accessed after the SPA loads config.json and the header provides Sign in/Sign out.
 *
 * Data flow (high level):
 * - Read id_token from sessionStorage (via getToken()).
 * - If token exists, UploadQueue will:
 *   - call backend initiate endpoint (creates a multipart upload session)
 *   - request pre-signed part URLs
 *   - upload parts directly to S3 via XHR
 *   - complete/abort via backend
 *
 * Learner note:
 * - Token presence is a *UI gate* only. Real enforcement happens server-side:
 *   - API Gateway validates JWT via Cognito authorizer
 *   - Lambdas enforce allowlist and (for admin endpoints) group membership
 *
 * Gotchas:
 * - This route does not proactively redirect to login by itself; it shows instructions instead.
 *   Other places (api.ts/useAuthGuard.ts) may trigger redirects when tokens expire or API returns 401.
 * - A token can exist even if the user is not allowlisted. In that case, UploadQueue/API calls will fail
 *   with 403 (Forbidden), and the UI should show a clear message (handled elsewhere).
 */

import type { AppConfig } from "../lib/types";
import { getToken } from "../lib/auth";
import { UploadQueue } from "../components/UploadQueue";

export function UploadRoute(props: { config: AppConfig }) {
  /**
   * Authentication state (client-side):
   * - getToken() reads the id_token stored in sessionStorage by auth.ts after login.
   * - If token is missing, we treat the user as "not signed in" and show guidance.
   *
   * Why read token here:
   * - Keeps routing simple: either show instructions or show the upload UI.
   * - UploadQueue assumes authentication and will call protected endpoints with the token.
   */
  const token = getToken();

  return (
    <main id="main" className="container app-shell" role="main">
      <section className="card">
        <div className="card-header">
          <h1 className="card-title">Upload</h1>

          {/* The subtitle describes the security model: sign-in + allowlist required. */}
          <p className="card-sub">
            Upload ZIP and JPEG files securely. You must sign in with an
            approved email address.
          </p>
        </div>
        <div className="card-body">
          {!token ? (
            <>
              {/* Not signed in:
                  - We don't start login automatically here.
                  - Instead we explain the requirement and point to the Sign in button in the header.
                  - This is a deliberate UX choice: the user can read context before redirecting.
              */}
              <div className="notice">
                <strong>Before you upload: </strong>
                Your email must be approved by an administrator.
                <p>
                  Use the <strong>Sign in</strong> button in the header to
                  create an account or sign in. If you are unable to create an
                  account or sign in and believe you should have access, please
                  contact the team that invited you to upload.
                </p>
                {/* Learner note:
                    - Account creation is still "self sign-up" at the Cognito level,
                      but the backend PreSignUp trigger blocks unapproved emails.
                    - This means users may see a signup failure even though the UI allowed them
                      to attempt it; that's expected and part of the allowlist design.
                */}
              </div>
            </>
          ) : (
            /**
             * Signed in:
             * - Render UploadQueue which contains the real upload workflow:
             *   initiate → part-url → PUT parts to S3 → complete/abort.
             *
             * Why pass config:
             * - config contains apiBaseUrl, cognito domain/clientId, and allowedTypes.
             * - Keeping config as a prop makes the component easy to test and avoids global state.
             */
            <UploadQueue config={props.config} />
          )}
        </div>
      </section>
    </main>
  );
}
